import { observer } from 'mobx-react';
import styled from 'styled-components';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import { fluoroAnglesFromCamera } from '@/engine/utils/geometryHelper';
import { useTheme } from '@mui/material/styles';
import type { OrientationPresetsController } from './OrientationPresets.controller';
import { IconButton, Tooltip } from '@mui/material';
import type { IViewPortOrientation } from '@/engine/app/interfaces';
import { OrientationPresetKind } from '@/engine/app/interfaces';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import { EditableCellTextField } from '../common/EditableCellTextField';
import type { IOrientationPreset } from '@/engine/app/models/viewEntity/view.entity.ts';
import type { FC } from 'react';

import { useAlgorithmValue } from '../common/useAlgorithmValue';

export const OrientationPresetTableRow: FC<{
  controller: OrientationPresetsController;
  preset: IOrientationPreset;
  index: number;
  vp3DOrientation: IViewPortOrientation;
  vpImgOrientation: IViewPortOrientation;
}> = observer(({ controller, preset, index, vp3DOrientation, vpImgOrientation }) => {
  const theme = useTheme();

  const orientation = useAlgorithmValue(preset.descriptor, DT.Orientation);

  const isSelected = (orientation: DT.Orientation): boolean => {
    return (
      orientation.value.top.angleTo(vp3DOrientation.topVector) < 0.001 &&
      orientation.value.dir.angleTo(vp3DOrientation.directionVector) < 0.001 &&
      orientation.value.top.angleTo(vpImgOrientation.topVector) < 0.001 &&
      orientation.value.dir.angleTo(vpImgOrientation.directionVector) < 0.001
    );
  };

  if (orientation === undefined) {
    return <ErrorIcon />;
  }

  const isItemSelected = isSelected(orientation);
  const angles = fluoroAnglesFromCamera(orientation.value.dir.clone().negate());
  const raoLao = angles.raoLao;
  const caudCran = angles.caudCran;
  const warning = Math.abs(raoLao) > 90 + 1e-4 || Math.abs(caudCran) > 90 + 1e-4;

  return (
    <TableRow
      sx={{
        '&:last-child td, &:last-child th': { border: 0 },
        '& td': {
          paddingLeft: 0.1,
          paddingRight: 0.1,
          paddingTop: 0.5,
          paddingBottom: 0.5,
          height: 42
        },
        '& td:first-child': { paddingLeft: 2 },
        '& td:last-child': { paddingLeft: 0.1, paddingRight: 0.6 },
        '&:hover': {
          backgroundColor: theme.customPalette.panels.tableRowHighlighted,
          color: theme.contrastFont ?? 'inherit'
        }
      }}
      key={preset.descriptor}
      data-testid={preset.descriptor + 'OrientationPreset'}
      selected={isItemSelected}
      onClick={(): void => {
        controller.set3DOrientationByDescriptor(preset.descriptor);
      }}
    >
      <TableCell sx={{ width: '22em' }} scope="row" key={preset.label + index}>
        {preset.kind === OrientationPresetKind.Predefined ? (
          <span>{preset.label}</span>
        ) : (
          <EditableCellTextField
            value={preset.label}
            onBlur={(e): void => {
              preset.setLabel(e.currentTarget.value);
            }}
          />
        )}
      </TableCell>
      <TableCell sx={{ width: '10em' }} align="left" key={'raolao' + index}>
        <AnglesLabelContainer $warning={warning}>
          <span>
            {raoLao >= 0 ? 'LAO' : 'RAO'}: {Math.abs(raoLao).toFixed() + '°'}
          </span>
        </AnglesLabelContainer>
      </TableCell>
      <TableCell sx={{ width: '10em' }} align="left" key={'rcaudcran' + index}>
        <AnglesLabelContainer $warning={warning}>
          <span>
            {caudCran >= 0 ? 'CRAN' : 'CAUD'}: {Math.abs(caudCran).toFixed() + '°'}
          </span>
        </AnglesLabelContainer>
      </TableCell>
      <TableCell sx={{ width: '2em' }} key={'delete' + index} align="right">
        <Tooltip title="Delete" placement="top">
          <span>
            {preset.kind === OrientationPresetKind.Custom && (
              <IconButton
                size="small"
                color="primary"
                onClick={(): void => {
                  // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
                  controller.deletePresetByDescriptor(preset.descriptor);
                }}
              >
                <DeleteOutlinedIcon />
              </IconButton>
            )}
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
});

const AnglesLabelContainer = styled.span<{ $warning: boolean }>`
  color: ${(props): string => (props.$warning ? props.theme.palette.error.main : 'inherit')};
`;
